import React, { useState, Component } from 'react';
import { Send, Phone, Mail, Calendar, Facebook, Instagram, Linkedin, MapPin } from 'lucide-react';
export const CallToAction = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    message: ''
  });
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = e => {
    e.preventDefault();
    // Create email template
    const recipient = 'info@callvanasolutions.com';
    const subject = 'New Contact Form Submission';
    const body = `Hello Team,
You have received a new message via the contact form on your website. Here are the details:
Name: ${formData.name}
Company Name: ${formData.company}
Message: ${formData.message}
Please follow up with the sender as soon as possible.
Best regards,
Your Website Contact Form`;
    // Create Gmail URL instead of mailto
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(recipient)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // Open Gmail in a new tab
    window.open(gmailUrl, '_blank');
    // Reset form
    setFormData({
      name: '',
      company: '',
      message: ''
    });
  };
  return <section id="contact" className="relative py-24 bg-black w-full" data-id="element-24">
      {/* Background elements */}
      <div className="absolute inset-0" data-id="element-25">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/50 to-black" data-id="element-26"></div>
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/asfalt-dark.png')] opacity-30" data-id="element-27"></div>
        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden" data-id="element-28">
          {Array.from({
          length: 30
        }).map((_, i) => <div key={i} className="absolute w-1 h-1 rounded-full bg-blue-light animate-floatSlow opacity-30" style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDuration: `${Math.random() * 10 + 10}s`,
          animationDelay: `${Math.random() * 5}s`,
          boxShadow: '0 0 10px 2px rgba(59, 123, 253, 0.3)'
        }} data-id="element-29"></div>)}
        </div>
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 z-5" data-id="element-30">
          <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSg1OSwgMTIzLCAyNTMsIDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIgLz48L3N2Zz4=')] opacity-20" data-id="element-31"></div>
        </div>
        {/* Glowing orbs */}
        <div className="absolute inset-0 z-0 overflow-hidden" data-id="element-32">
          <div className="absolute w-96 h-96 rounded-full bg-blue-primary/5 blur-3xl animate-pulse" style={{
          top: '20%',
          left: '10%',
          animationDuration: '15s'
        }} data-id="element-33"></div>
          <div className="absolute w-96 h-96 rounded-full bg-blue-light/5 blur-3xl animate-pulse" style={{
          bottom: '10%',
          right: '10%',
          animationDuration: '20s'
        }} data-id="element-34"></div>
        </div>
      </div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-35">
        {/* Calendly Button - Simplified version */}
        <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-36">
          <h3 className="text-2xl font-bold mb-6 animate-fadeIn" data-id="element-37">
            Schedule a{' '}
            <span className="text-blue-light" data-id="element-38">Free Consultation</span>
          </h3>
          <a href="https://calendly.com/callvanasolutions-info/30min" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-primary to-blue-light text-white font-bold rounded-md hover:from-blue-dark hover:to-blue-primary transition-all transform hover:scale-105 uppercase tracking-wide text-sm md:text-base relative overflow-hidden group" data-id="element-39">
            <span className="relative z-10 flex items-center" data-id="element-40">
              <Calendar size={18} className="mr-2" data-id="element-41" />
              Book Your Call Now
            </span>
            <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300" data-id="element-42"></span>
          </a>
        </div>
        <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-43">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 animate-fadeIn" data-id="element-44">
            Let's{' '}
            <span className="text-blue-light relative" data-id="element-45">
              Connect
              <span className="absolute -inset-1 bg-blue-light/10 blur-md -z-10 rounded-lg" data-id="element-46"></span>
            </span>
          </h2>
          <p className="text-gray-300 text-lg animate-fadeIn" style={{
          animationDelay: '0.2s'
        }} data-id="element-47">
            Ready to transform your sales process? Our experts are here to help
            you reach new heights.
          </p>
        </div>
        <div className="max-w-5xl mx-auto bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl overflow-hidden shadow-2xl transform transition-all duration-700 hover:shadow-blue-primary/20" data-id="element-48">
          <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-49">
            {/* Left side - Contact form */}
            <div className="p-8 lg:p-12 relative" data-id="element-50">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-primary to-blue-light" data-id="element-51"></div>
              <h3 className="text-2xl font-bold mb-6 animate-fadeIn" data-id="element-52">
                Contact Our Team
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5" data-id="element-53">
                <div data-id="element-54">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-55">
                    Your Name
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-800/80 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-transparent text-white transition-all duration-300" placeholder="Enter your name" data-id="element-56" />
                </div>
                <div data-id="element-57">
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-58">
                    Company Name
                  </label>
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-800/80 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-transparent text-white transition-all duration-300" placeholder="Enter your company name" data-id="element-59" />
                </div>
                <div data-id="element-60">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-61">
                    Your Message
                  </label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} className="w-full px-4 py-3 bg-gray-800/80 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-transparent text-white transition-all duration-300 min-h-[120px]" placeholder="Tell us about your needs" data-id="element-62"></textarea>
                </div>
                <button type="submit" className="w-full py-4 px-6 bg-gradient-to-r from-blue-primary to-blue-light hover:from-blue-dark hover:to-blue-primary text-white font-bold rounded-md transition-all transform hover:scale-[1.02] flex items-center justify-center group relative overflow-hidden" data-id="element-63">
                  <span className="absolute top-0 left-0 w-full h-full bg-white/10 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300" data-id="element-64"></span>
                  <Send size={18} className="mr-2 relative z-10 animate-pulse" data-id="element-65" />
                  <span className="relative z-10" data-id="element-66">Send Message</span>
                </button>
              </form>
            </div>
            {/* Right side - Contact info & animated elements */}
            <div className="p-8 lg:p-12 bg-gradient-to-br from-blue-primary/10 to-blue-light/10 relative overflow-hidden" data-id="element-67">
              {/* Animated background elements */}
              <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-blue-primary/10 animate-pulse" style={{
              animationDuration: '6s'
            }} data-id="element-68"></div>
              <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-blue-light/5 animate-pulse" style={{
              animationDuration: '8s'
            }} data-id="element-69"></div>
              <div className="relative h-full flex flex-col" data-id="element-70">
                <h3 className="text-2xl font-bold mb-8 animate-fadeIn" data-id="element-71">
                  Get In <span className="text-blue-light" data-id="element-72">Touch</span>
                </h3>
                <div className="space-y-8 mb-12" data-id="element-73">
                  <div className="flex items-start transform transition-all duration-500 hover:translate-x-2" data-id="element-74">
                    <div className="flex-shrink-0 mt-1" data-id="element-75">
                      <div className="w-10 h-10 rounded-full bg-blue-primary/20 flex items-center justify-center" data-id="element-76">
                        <Phone size={18} className="text-blue-light" data-id="element-77" />
                      </div>
                    </div>
                    <div className="ml-4" data-id="element-78">
                      <h4 className="text-white font-medium" data-id="element-79">Call Us</h4>
                      <p className="text-gray-300 text-sm mt-1" data-id="element-80">
                        +1 (786) 577-7533
                      </p>
                      <p className="text-gray-400 text-xs mt-1" data-id="element-81">
                        Monday-Friday, 9am-5pm EST
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start transform transition-all duration-500 hover:translate-x-2" data-id="element-82">
                    <div className="flex-shrink-0 mt-1" data-id="element-83">
                      <div className="w-10 h-10 rounded-full bg-blue-primary/20 flex items-center justify-center" data-id="element-84">
                        <MapPin size={18} className="text-blue-light" data-id="element-85" />
                      </div>
                    </div>
                    <div className="ml-4" data-id="element-86">
                      <h4 className="text-white font-medium" data-id="element-87">Address</h4>
                      <p className="text-gray-300 text-sm mt-1" data-id="element-88">
                        30 N Gould St, Sheridan, WY 82801
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start transform transition-all duration-500 hover:translate-x-2" data-id="element-89">
                    <div className="flex-shrink-0 mt-1" data-id="element-90">
                      <div className="w-10 h-10 rounded-full bg-blue-primary/20 flex items-center justify-center" data-id="element-91">
                        <Mail size={18} className="text-blue-light" data-id="element-92" />
                      </div>
                    </div>
                    <div className="ml-4" data-id="element-93">
                      <h4 className="text-white font-medium" data-id="element-94">Email Us</h4>
                      <p className="text-gray-300 text-sm mt-1" data-id="element-95">
                        info@callvanasolutions.com
                      </p>
                      <p className="text-gray-400 text-xs mt-1" data-id="element-96">
                        We'll respond within 24 hours
                      </p>
                    </div>
                  </div>
                </div>
                {/* Social Media Icons */}
                <div className="mt-auto mb-6" data-id="element-97">
                  <h4 className="text-white font-medium mb-3" data-id="element-98">
                    Connect With Us
                  </h4>
                  <div className="flex space-x-4" data-id="element-99">
                    <a href="https://www.instagram.com/callvanasolutions" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-primary/20 flex items-center justify-center transition-all duration-300 hover:bg-blue-primary/40 hover:scale-110 group" aria-label="Instagram" data-id="element-100">
                      <Instagram size={18} className="text-blue-light group-hover:text-white" data-id="element-101" />
                    </a>
                    <a href="https://www.facebook.com/profile.php?id=61581422727746" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-primary/20 flex items-center justify-center transition-all duration-300 hover:bg-blue-primary/40 hover:scale-110 group" aria-label="Facebook" data-id="element-102">
                      <Facebook size={18} className="text-blue-light group-hover:text-white" data-id="element-103" />
                    </a>
                    <a href="https://www.linkedin.com/company/callvana-solutions/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-primary/20 flex items-center justify-center transition-all duration-300 hover:bg-blue-primary/40 hover:scale-110 group" aria-label="LinkedIn" data-id="element-104">
                      <Linkedin size={18} className="text-blue-light group-hover:text-white" data-id="element-105" />
                    </a>
                  </div>
                </div>
                {/* Digital circuit pattern */}
                <div className="mt-auto relative" data-id="element-106">
                  <div className="w-full h-32 opacity-20" style={{
                  background: `url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTAgOTBMNTAgOTBMNTAgNTBMOTAgNTBMOTAgMTBMMTMwIDEwTDEzMCA1MEwxNzAgNTBMMTcwIDkwIiBzdHJva2U9IiMzQjdCRkQiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==')`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center'
                }} data-id="element-107"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};